import React from "react";

interface CreditBatch {
  balance: number;
  initial_amount: number;
  expires_at: string;
}

interface CreditMeterProps {
  // Active (unexpired) credit batches for one credit type, soonest-expiring
  // first - the same order they're drained in, so segment order matches
  // consumption order.
  batches: CreditBatch[];
  // Renders a single fully-filled bar instead of segments, for a tier whose
  // quota is a display sentinel rather than a real batch total.
  unlimited?: boolean;
}

// A batch expiring this soon is called out in warning/danger colors instead
// of the ordinal primary ramp, since draining before it expires stops being
// a given.
const WARNING_THRESHOLD_HOURS = 24 * 7;
const CRITICAL_THRESHOLD_HOURS = 24;

type ExpiryStatus = "critical" | "warning" | "normal";

function getExpiryStatus(expiresAt: string): ExpiryStatus {
  const hoursRemaining = (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60);
  if (Number.isNaN(hoursRemaining)) return "normal";
  if (hoursRemaining <= CRITICAL_THRESHOLD_HOURS) return "critical";
  if (hoursRemaining <= WARNING_THRESHOLD_HOURS) return "warning";
  return "normal";
}

// Showing every batch as its own segment stops reading as an ordered ramp
// past a handful of steps (validated: a single hue's lightness steps stop
// clearing a safe adjacent-contrast gap beyond ~3). Batches are typically
// 1-2 at a time (monthly replenishment draining soonest-expiring first), so
// this is a rare-case fold, not a routine truncation - anything past the
// cap is summed into one trailing segment rather than dropped.
const MAX_SEGMENTS = 3;

function formatExpiry(expiresAt: string): string {
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return "unknown expiration";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// Ordinal ramp: primary hue, monotone lightness steps - batch order carries
// meaning (soonest-expiring drains first), so this is an ordinal encoding,
// not a categorical one. The soonest-expiring batch stays fully saturated;
// later batches lighten progressively via color-mix so the ramp reads as a
// sequence rather than distinct, unordered categories. A batch nearing its
// own expiration breaks out of the ramp into warning/danger instead, since
// that's a status signal, not a position in the sequence. Validated with
// scripts/validate_palette.js --ordinal against this project's primary hue
// at up to MAX_SEGMENTS steps.
function segmentColor(status: ExpiryStatus, index: number, count: number): string {
  if (status === "critical") return "hsl(var(--er))";
  if (status === "warning") return "hsl(var(--wa))";
  const lightenPct = count > 1 ? (index / (count - 1)) * 55 : 0;
  return `color-mix(in oklch, hsl(var(--p)) ${100 - lightenPct}%, white ${lightenPct}%)`;
}

interface DisplaySegment {
  key: string;
  balance: number;
  label: string;
  expiresAt: string;
}

function buildDisplaySegments(active: CreditBatch[]): DisplaySegment[] {
  if (active.length <= MAX_SEGMENTS) {
    return active.map((batch, index) => ({
      key: `${batch.expires_at}-${index}`,
      balance: batch.balance,
      label: `${batch.balance.toLocaleString()} credits · expires ${formatExpiry(batch.expires_at)}`,
      expiresAt: batch.expires_at,
    }));
  }
  const shown = active.slice(0, MAX_SEGMENTS - 1);
  const folded = active.slice(MAX_SEGMENTS - 1);
  const foldedBalance = folded.reduce((sum, batch) => sum + batch.balance, 0);
  const nextExpiry = folded.map((batch) => batch.expires_at).sort()[0];
  return [
    ...shown.map((batch, index) => ({
      key: `${batch.expires_at}-${index}`,
      balance: batch.balance,
      label: `${batch.balance.toLocaleString()} credits · expires ${formatExpiry(batch.expires_at)}`,
      expiresAt: batch.expires_at,
    })),
    {
      key: "folded",
      balance: foldedBalance,
      label: `${foldedBalance.toLocaleString()} credits across ${folded.length} more batches · next expires ${formatExpiry(nextExpiry)}`,
      expiresAt: nextExpiry,
    },
  ];
}

const CreditMeter: React.FC<CreditMeterProps> = ({ batches, unlimited = false }) => {
  if (unlimited) {
    return (
      <div className="h-2 w-full overflow-hidden rounded-full bg-base-300">
        <div className="h-full w-full" style={{ backgroundColor: "hsl(var(--p))" }} />
      </div>
    );
  }

  const active = batches.filter((batch) => batch.balance > 0);
  const totalInitial = batches.reduce((sum, batch) => sum + (batch.initial_amount || 0), 0);

  if (active.length === 0 || totalInitial <= 0) {
    return <div className="h-2 w-full rounded-full bg-base-300" />;
  }

  // Colors key off drain order (soonest-expiring = index 0, fully
  // saturated), then the row itself is flipped so soonest-expiring lands on
  // the right - matching drain direction (rightmost drains first) instead
  // of reading order.
  const segments = buildDisplaySegments(active).map((segment, index, all) => ({
    ...segment,
    color: segmentColor(getExpiryStatus(segment.expiresAt), index, all.length),
  }));
  const displaySegments = [...segments].reverse();

  // No overflow-hidden here: the tooltip pops up via bottom-full, i.e.
  // outside this row's own box, and an overflow-hidden ancestor would clip
  // it. The pill shape doesn't need it anyway - the first/last segment
  // already carry their own rounded outer corner below.
  return (
    <div className="flex h-2 w-full gap-[2px] rounded-full bg-base-300">
      {displaySegments.map((segment) => (
        <div
          key={segment.key}
          tabIndex={0}
          className="group relative h-full outline-none first:rounded-l-full last:rounded-r-full focus-visible:ring-2 focus-visible:ring-base-content/40"
          style={{
            width: `${Math.min((segment.balance / totalInitial) * 100, 100)}%`,
            backgroundColor: segment.color,
          }}
          aria-label={segment.label}
        >
          <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded bg-neutral px-2 py-1 text-xs text-neutral-content opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
            {segment.label}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CreditMeter;
